import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Ban, AlertTriangle, CheckCircle2, MinusCircle, X } from 'lucide-react';
import { CORES_RISCO, mesPorExtenso, dataBr } from './AutoavaliacaoConsulta';

// =====================================================================
// Autoavaliação de Controles — o DETALHE de uma autoavaliação gravada.
//
// Mostra as 22 respostas por seção e, nas marcadas "Não", os 4 campos do plano
// de ação — que na fase 1 são REGISTRO (não há baixa nem cobrança; a fila de
// acompanhamento está no PLANO como fase 2).
//
// ⚠️ As não conformidades vêm PRIMEIRO, num bloco próprio. Quem abre isto quer
//    saber o que está errado; obrigar a percorrer 22 perguntas para achar 3
//    "Não" transformaria a tela em papel.
// =====================================================================

const API_BASE = '/api/operacional/autoavaliacao';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const COR = '#475569';

const AutoavaliacaoDetalhe = ({ id, podeCancelar, onVoltar, onCancelou }) => {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [modalCancelar, setModalCancelar] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [cancelando, setCancelando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const r = await fetch(`${API_BASE}/${id}`, { headers: auth() });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Falha ao carregar.');
      setDados(d);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  const cancelar = async () => {
    setCancelando(true);
    setErro('');
    try {
      const r = await fetch(`${API_BASE}/${id}/cancelar`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Falha ao cancelar.');
      setModalCancelar(false);
      onCancelou?.();
    } catch (e) {
      setErro(e.message);
    } finally {
      setCancelando(false);
    }
  };

  const voltar = (
    <div style={{ marginBottom: 16 }}>
      <button type="button" onClick={onVoltar} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 8, fontWeight: 700, cursor: 'pointer', color: '#475569',
        fontFamily: 'inherit', fontSize: 13,
      }}>
        <ArrowLeft size={16} /> Voltar
      </button>
    </div>
  );

  if (loading) return <div>{voltar}<p style={{ color: '#64748b' }}>Carregando…</p></div>;
  if (!dados) return <div>{voltar}<p style={{ color: '#dc2626' }}>{erro}</p></div>;

  const a = dados.autoavaliacao;
  const faixa = CORES_RISCO[a.CLASSIFICACAO] || CORES_RISCO.NA;
  const cancelada = Boolean(a.CANCELADO_EM);
  const naoConformidades = dados.respostas.filter((r) => !r.NAO_APLICAVEL && r.ATENDE === 0);

  // Agrupa as respostas por seção, preservando a ordem que veio do backend.
  const secoes = [];
  for (const r of dados.respostas) {
    const ultima = secoes[secoes.length - 1];
    if (!ultima || ultima.id !== r.ID_AREA) secoes.push({ id: r.ID_AREA, nome: r.AREA, itens: [r] });
    else ultima.itens.push(r);
  }

  return (
    <div>
      <style>{'.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      {voltar}

      {erro && (
        <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>
          {erro}
        </div>
      )}

      {/* Cabeçalho */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderLeft: `4px solid ${cancelada ? '#94a3b8' : faixa.cor}`,
        borderRadius: 12, padding: 20, marginBottom: 16,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
              {a.LOJA} · {mesPorExtenso(a.ANOMES)}
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
              Respondida em {dataBr(a.DATA_PREENCHIMENTO)} por <strong>{a.GERENTE_NOME || '—'}</strong>
              {' · '}{a.NOME_VERSAO}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', letterSpacing: 0.5 }}>RISCO</div>
            <div style={{
              fontSize: 24, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
              color: cancelada ? '#94a3b8' : faixa.cor,
            }}>
              {Number(a.PERCENTUAL_RISCO).toFixed(2)}%
            </div>
            <div style={{
              display: 'inline-block', marginTop: 4, padding: '3px 10px', borderRadius: 999,
              background: cancelada ? '#f1f5f9' : faixa.bg, color: cancelada ? '#64748b' : faixa.cor,
              fontWeight: 800, fontSize: 12,
            }}>
              {faixa.icone} {faixa.nome}
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
              {a.QTD_NAO} “Não” de {a.QTD_APLICAVEL} aplicáveis
              {a.QTD_NA > 0 && <> · {a.QTD_NA} N/A fora da conta</>}
            </div>
          </div>
        </div>

        {cancelada && (
          <div style={{
            marginTop: 14, padding: '10px 12px', background: '#f1f5f9',
            border: '1px solid #e2e8f0', borderRadius: 8, color: '#475569', fontSize: 13,
          }}>
            <Ban size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
            <strong>Cancelada</strong> por {a.CANCELADO_POR_NOME || '—'} em{' '}
            {dataBr(a.CANCELADO_EM)} — “{a.CANCELADO_MOTIVO}”
          </div>
        )}

        {a.OBSERVACAO_GERAL && (
          <div style={{
            marginTop: 14, padding: '10px 12px', background: '#f8fafc',
            border: '1px solid #e2e8f0', borderRadius: 8, color: '#334155',
            fontSize: 13, whiteSpace: 'pre-line',
          }}>
            <strong style={{ color: COR }}>Observações gerais:</strong> {a.OBSERVACAO_GERAL}
          </div>
        )}

        {podeCancelar && !cancelada && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
            <button type="button" onClick={() => { setMotivo(''); setModalCancelar(true); }} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              background: '#fff', color: '#b91c1c', border: '1px solid #fca5a5',
              borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
            }}>
              <Ban size={15} /> Cancelar e responder de novo
            </button>
            <span style={{ marginLeft: 10, fontSize: 12, color: '#64748b' }}>
              A resposta atual fica registrada, com o motivo — nada é apagado.
            </span>
          </div>
        )}
      </div>

      {/* As não conformidades primeiro — é o que se quer ver ao abrir. */}
      {naoConformidades.length > 0 && (
        <div style={{
          background: '#fff', border: '1px solid #fca5a5', borderRadius: 12,
          padding: 20, marginBottom: 16,
        }}>
          <h4 style={{
            margin: '0 0 14px', fontSize: 15, fontWeight: 800, color: '#b91c1c',
            display: 'inline-flex', alignItems: 'center', gap: 7,
          }}>
            <AlertTriangle size={17} /> {naoConformidades.length} não conformidade(s) e o plano de ação
          </h4>
          <div style={{ display: 'grid', gap: 12 }}>
            {naoConformidades.map((r) => (
              <div key={r.ID_PERGUNTA} style={{ background: '#fef2f2', borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 13.5, color: '#0f172a', fontWeight: 600, lineHeight: 1.5 }}>
                  {r.TEXTO}
                </div>
                <div style={{ fontSize: 11, color: '#7f1d1d', fontWeight: 700, margin: '4px 0 10px' }}>
                  {r.AREA}
                </div>
                <div style={{
                  display: 'grid', gap: 10,
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                }}>
                  <Campo rotulo="Motivo" valor={r.MOTIVO} largo />
                  <Campo rotulo="Plano de ação" valor={r.PLANO_ACAO} largo />
                  <Campo rotulo="Responsável" valor={r.RESPONSAVEL} />
                  <Campo rotulo="Prazo" valor={dataBr(r.PRAZO)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Todas as respostas, por seção */}
      {secoes.map((s, i) => (
        <div key={s.id} style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
          padding: 20, marginBottom: 16,
        }}>
          <h4 style={{
            margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: '#0f172a',
            paddingBottom: 10, borderBottom: '1px solid #f1f5f9',
          }}>
            <span style={{ color: COR }}>{i + 1}.</span> {s.nome}
          </h4>
          <div style={{ display: 'grid', gap: 8 }}>
            {s.itens.map((r) => {
              const na = Boolean(r.NAO_APLICAVEL);
              const sim = !na && r.ATENDE === 1;
              return (
                <div key={r.ID_PERGUNTA} style={{
                  display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto',
                  gap: 12, alignItems: 'start', padding: 10,
                  background: na ? '#f8fafc' : (sim ? '#f0fdf4' : '#fef2f2'),
                  borderRadius: 8,
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.5 }}>{r.TEXTO}</div>
                  </div>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
                    whiteSpace: 'nowrap',
                    background: na ? '#f1f5f9' : (sim ? '#d1fae5' : '#fee2e2'),
                    color: na ? '#64748b' : (sim ? '#065f46' : '#991b1b'),
                  }}>
                    {na ? <><MinusCircle size={13} /> N/A</>
                      : sim ? <><CheckCircle2 size={13} /> Sim</>
                        : <><AlertTriangle size={13} /> Não</>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Pop-up do cancelamento — o motivo é obrigatório, e é o que separa
          "correção registrada" de "edição disfarçada". */}
      {modalCancelar && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 50, padding: 20,
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: 24,
            width: '100%', maxWidth: 520, boxShadow: '0 20px 40px rgba(0,0,0,.25)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <h4 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
                Cancelar a autoverificação de {mesPorExtenso(a.ANOMES)}?
              </h4>
              <button type="button" onClick={() => setModalCancelar(false)} style={{
                border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', padding: 4,
              }}>
                <X size={18} />
              </button>
            </div>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
              A resposta atual <strong>não é apagada</strong>: fica na lista marcada como cancelada,
              com o seu nome e o motivo. O mês volta a ficar disponível para ser respondido.
            </p>
            <label style={{ fontSize: 12, fontWeight: 800, color: '#7f1d1d' }}>
              Motivo do cancelamento *
            </label>
            <textarea
              rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex.: marquei “Sim” na pergunta 7 por engano; o caderno estava incompleto."
              style={{
                width: '100%', boxSizing: 'border-box', marginTop: 4, padding: 10,
                border: '1px solid #cbd5e1', borderRadius: 8, fontFamily: 'inherit',
                fontSize: 13, resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" onClick={() => setModalCancelar(false)} style={{
                padding: '9px 16px', background: '#fff', color: '#475569',
                border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 700,
                cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
              }}>
                Voltar
              </button>
              <button
                type="button" onClick={cancelar}
                disabled={cancelando || motivo.trim().length < 5}
                style={{
                  padding: '9px 16px', border: 'none', borderRadius: 8,
                  background: (cancelando || motivo.trim().length < 5) ? '#cbd5e1' : '#b91c1c',
                  color: '#fff', fontWeight: 800, fontSize: 13, fontFamily: 'inherit',
                  cursor: (cancelando || motivo.trim().length < 5) ? 'not-allowed' : 'pointer',
                }}
              >
                {cancelando ? 'Cancelando…' : 'Confirmar cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Campo = ({ rotulo, valor, largo = false }) => (
  <div style={largo ? { gridColumn: '1 / -1' } : undefined}>
    <div style={{ fontSize: 11, fontWeight: 800, color: '#7f1d1d' }}>{rotulo}</div>
    <div style={{
      marginTop: 3, padding: '7px 9px', background: '#fff', border: '1px solid #fecaca',
      borderRadius: 6, fontSize: 13, color: '#334155', whiteSpace: 'pre-line',
    }}>
      {valor || '—'}
    </div>
  </div>
);

export default AutoavaliacaoDetalhe;
